import { db, auth } from '../firebase';
import { collection, addDoc, updateDoc, doc, query, where, getDocs, orderBy } from 'firebase/firestore';
import { linkedinOAuth } from './oauth/linkedinOAuth';

export interface SocialShare {
  id: string;
  userId: string;
  userName: string;
  platform: 'linkedin' | 'twitter' | 'facebook' | 'instagram';
  content: string;
  timestamp: Date;
  pointsEarned: number;
  status: 'pending' | 'shared' | 'failed';
  shareUrl?: string;
}

export interface SocialPost {
  id: string;
  content: string;
  platform: 'linkedin' | 'twitter' | 'instagram' | 'facebook';
  scheduledDate: Date;
  status: 'draft' | 'scheduled' | 'published' | 'failed';
  engagement?: {
    likes: number;
    shares: number;
    comments: number;
  };
}

export interface SocialAnalytics {
  totalPosts: number;
  totalEngagement: number;
  averageEngagement: number;
  topPerformingPost: SocialPost | null;
  platformBreakdown: {
    linkedin: number;
    twitter: number;
    instagram: number;
    facebook: number;
  };
}

export interface SocialMediaConnection {
  platform: 'linkedin' | 'twitter' | 'facebook' | 'instagram';
  isConnected: boolean;
  accessToken?: string;
  refreshToken?: string;
  expiresAt?: Date;
  profileUrl?: string;
  username?: string;
  lastSync?: Date;
}

// Additional interfaces for social sharing components
export interface ShareContent {
  title: string;
  description: string;
  url?: string;
  imageUrl?: string;
  hashtags?: string[];
  platform?: SocialPlatform;
}

export interface ShareResult {
  success: boolean;
  shareUrl?: string;
  error?: string;
  platform: SocialPlatform;
  timestamp: Date;
}

export interface SocialPlatform {
  name: string;
  displayName: string;
  icon: string;
  color: string;
  enabled: boolean;
  accessToken?: string;
}

export interface UserProfile {
  uid: string;
  displayName: string;
  email: string;
  photoURL?: string;
  bio?: string;
  company?: string;
  linkedin?: string;
  joinDate: Date;
  points: number;
  socialSharingEnabled: boolean;
  sharingFrequency: 3 | 5 | 7;
  lastSharedDate?: Date;
  totalShares: number;
  socialMediaConnections?: SocialMediaConnection[];
  linkedinAccessToken?: string;
  twitterAccessToken?: string;
  facebookAccessToken?: string;
  instagramAccessToken?: string;
  
  // Goals & Learning Goals data
  goals?: {
    clients?: boolean;
    partnerships?: boolean;
    hiring?: boolean;
    funding?: boolean;
    speaking?: boolean;
    content?: boolean;
    personalBrand?: boolean;
    productLaunch?: boolean;
    community?: boolean;
    networking?: boolean; // legacy compatibility
    learning?: boolean;   // legacy compatibility
    timeframe?: string;
    kpis?: {
      leads?: number;
      revenue?: number;
      followers?: number;
    };
    notes?: string;
  };
  learningGoals?: string[];
  contentPrefs?: {
    formatPrefs?: string[];
    timeBudget?: string;
    notes?: string;
  };
  lastOnboardingStep?: string;
  updatedAt?: Date;
}

export interface PodcastSession {
  id: string;
  title: string;
  date: string;
  participants: string[];
  maxParticipants: number;
  description: string;
  host: string;
  category: string;
}

class SocialSharingService {
  private platforms: SocialPlatform[] = [
    {
      name: 'linkedin',
      displayName: 'LinkedIn',
      icon: '💼',
      color: '#0077B5',
      enabled: true
    },
    {
      name: 'twitter',
      displayName: 'Twitter',
      icon: '🐦',
      color: '#1DA1F2',
      enabled: true
    },
    {
      name: 'facebook',
      displayName: 'Facebook',
      icon: '📘',
      color: '#4267B2',
      enabled: true
    },
    {
      name: 'instagram',
      displayName: 'Instagram',
      icon: '📷',
      color: '#E4405F',
      enabled: true
    }
  ];

  // Get available platforms
  getAvailablePlatforms(): SocialPlatform[] {
    return this.platforms.filter(platform => platform.enabled);
  }

  // Get specific platform
  getPlatform(platformName: string): SocialPlatform | undefined {
    return this.platforms.find(platform => platform.name === platformName);
  }

  // Set platform enabled status
  setPlatformEnabled(platformName: string, enabled: boolean): void {
    const platform = this.platforms.find(p => p.name === platformName);
    if (platform) {
      platform.enabled = enabled;
    }
  }

  // Create quote share content
  createQuoteShareContent(quote: string, author?: string): ShareContent {
    return {
      title: 'Quote',
      description: `"${quote}"${author ? ` - ${author}` : ''}`,
      hashtags: ['#quote', '#inspiration', '#motivation']
    };
  }

  // Create content share content
  createContentShareContent(title: string, description: string, url?: string, imageUrl?: string): ShareContent {
    return {
      title,
      description,
      url,
      imageUrl,
      hashtags: ['#content', '#share']
    };
  }

  // Share to specific platform
  async shareToPlatform(platformName: string, content: ShareContent): Promise<ShareResult> {
    const platform = this.getPlatform(platformName);
    if (!platform) {
      return {
        success: false,
        error: 'Platform not found',
        platform: { name: platformName, displayName: platformName, icon: '❓', color: '#000', enabled: false },
        timestamp: new Date()
      };
    }

    const contentText = `${content.title}\n\n${content.description}${content.hashtags ? '\n\n' + content.hashtags.join(' ') : ''}`;

    try {
      let result;
      switch (platformName) {
        case 'linkedin':
          result = await this.shareToLinkedIn(contentText, platform.accessToken);
          break;
        case 'twitter':
          result = await this.shareToTwitter(contentText, platform.accessToken);
          break;
        case 'facebook':
          result = await this.shareToFacebook(contentText, platform.accessToken);
          break;
        case 'instagram':
          result = await this.shareToInstagram(contentText, platform.accessToken);
          break;
        default:
          result = { success: false, error: 'Platform not supported' };
      }

      return {
        success: result.success,
        shareUrl: result.shareUrl,
        error: result.error,
        platform,
        timestamp: new Date()
      };
    } catch (error) {
      return {
        success: false,
        error: 'Sharing failed',
        platform,
        timestamp: new Date()
      };
    }
  }

  // Share to multiple platforms
  async shareToMultiplePlatforms(platformNames: string[], content: ShareContent): Promise<ShareResult[]> {
    const promises = platformNames.map(platformName => this.shareToPlatform(platformName, content));
    return Promise.all(promises);
  }

  // LinkedIn API Integration
  async shareToLinkedIn(content: string, accessToken?: string): Promise<{ success: boolean; shareUrl?: string; error?: string }> {
    try {
      // Get current user ID
      const userId = auth.currentUser?.uid;
      if (!userId) {
        return { success: false, error: 'User not authenticated' };
      }

      // Use the linkedinOAuth service to post
      console.log('📤 Posting to LinkedIn via OAuth service...');
      const result = await linkedinOAuth.postToLinkedIn(content, userId);
      
      if (result.success) {
        console.log('✅ LinkedIn post successful:', result.shareUrl);
      } else {
        console.error('❌ LinkedIn post failed:', result.error);
      }
      
      return result;
      
    } catch (error) {
      console.error('❌ LinkedIn sharing error:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'LinkedIn sharing failed' 
      };
    }
  }

  // Twitter API Integration
  async shareToTwitter(content: string, accessToken?: string): Promise<{ success: boolean; shareUrl?: string; error?: string }> {
    if (!accessToken) {
      return { success: false, error: 'Twitter access token not available' };
    }

    try {
      // Twitter API v2 endpoint
      const response = await fetch('https://api.twitter.com/2/tweets', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          text: content.substring(0, 280) // Twitter character limit
        })
      });

      if (response.ok) {
        const result = await response.json();
        return { success: true, shareUrl: `https://twitter.com/user/status/${result.data.id}` };
      } else {
        return { success: false, error: 'Twitter API error' };
      }
    } catch (error) {
      console.error('Twitter sharing error:', error);
      return { success: false, error: 'Twitter sharing failed' };
    }
  }

  // Facebook API Integration
  async shareToFacebook(content: string, accessToken?: string): Promise<{ success: boolean; shareUrl?: string; error?: string }> {
    if (!accessToken) {
      return { success: false, error: 'Facebook access token not available' };
    }

    try {
      // Facebook Graph API endpoint
      const response = await fetch(`https://graph.facebook.com/v18.0/me/feed`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: content,
          access_token: accessToken
        })
      });

      if (response.ok) {
        const result = await response.json();
        return { success: true, shareUrl: `https://facebook.com/${result.id}` };
      } else {
        return { success: false, error: 'Facebook API error' };
      }
    } catch (error) {
      console.error('Facebook sharing error:', error);
      return { success: false, error: 'Facebook sharing failed' };
    }
  }

  // Instagram API Integration
  async shareToInstagram(content: string, accessToken?: string): Promise<{ success: boolean; shareUrl?: string; error?: string }> {
    if (!accessToken) {
      return { success: false, error: 'Instagram access token not available' };
    }

    try {
      // Instagram Basic Display API
      const response = await fetch(`https://graph.instagram.com/v18.0/me/media`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          caption: content,
          access_token: accessToken
        })
      });

      if (response.ok) {
        const result = await response.json();
        return { success: true, shareUrl: `https://instagram.com/p/${result.id}` };
      } else {
        return { success: false, error: 'Instagram API error' };
      }
    } catch (error) {
      console.error('Instagram sharing error:', error);
      return { success: false, error: 'Instagram sharing failed' };
    }
  }

  // TikTok API Integration
  async shareToTikTok(content: string, accessToken?: string): Promise<{ success: boolean; shareUrl?: string; error?: string }> {
    if (!accessToken) {
      return { success: false, error: 'TikTok access token not available' };
    }

    try {
      // TikTok API endpoint (mock implementation)
      // In production, this would use TikTok's official API
      console.log('Sharing to TikTok:', content);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Mock successful response
      const mockVideoId = Math.random().toString(36).substring(7);
      return { 
        success: true, 
        shareUrl: `https://tiktok.com/@superkonnected/video/${mockVideoId}` 
      };
    } catch (error) {
      console.error('TikTok sharing error:', error);
      return { success: false, error: 'TikTok sharing failed' };
    }
  }

  // Generate social media content for podcast sessions
  generateSocialContent(session: PodcastSession, platform: 'linkedin' | 'twitter' | 'facebook' | 'instagram' | 'tiktok'): string {
    const baseContent = `🎙️ Nieuwe podcast sessie: "${session.title}"`;
    const description = session.description;
    const date = new Date(session.date).toLocaleDateString('nl-NL');
    const host = session.host;
    const hashtags = '#SuperKonnected #Podcast #Networking #Business';
    const website = 'superkonnected.nl';

    switch (platform) {
      case 'linkedin':
        return `${baseContent}\n\n${description}\n\n📅 ${date}\n👥 Host: ${host}\n\n${hashtags}\n\nJoin de community: ${website}`;
      
      case 'twitter':
        return `${baseContent}\n\n📅 ${date}\n👥 ${host}\n\n${hashtags}`;
      
      case 'facebook':
        return `${baseContent}\n\n${description}\n\n📅 ${date}\n👥 Host: ${host}\n\nSuperKonnected - Van netwerken naar verbinden`;
      
      case 'instagram':
        return `${baseContent}\n\n📅 ${date}\n👥 Host: ${host}\n\n${hashtags}`;
      
      case 'tiktok':
        return `${baseContent}\n\n📅 ${date}\n👥 Host: ${host}\n\n${hashtags}\n\n#fyp #viral #trending`;
      
      default:
        return baseContent;
    }
  }

  // Generate social media content for episodes
  generateEpisodeContent(episode: any, platform: 'linkedin' | 'twitter' | 'facebook' | 'instagram' | 'tiktok'): string {
    const baseContent = `🎧 Nieuwe SuperKonnected Episode!\n\n"${episode.title}"\n\n${episode.description}`;
    const date = new Date(episode.date).toLocaleDateString('nl-NL');
    const guest = episode.guest;
    const duration = episode.duration;
    const hashtags = '#SuperKonnected #Podcast #Ondernemers #Business #Netwerken';
    
    // Add platform-specific links
    let links = '';
    if (episode.spotifyUrl) links += `\n🎵 Spotify: ${episode.spotifyUrl}`;
    if (episode.youtubeUrl) links += `\n📺 YouTube: ${episode.youtubeUrl}`;
    
    switch (platform) {
      case 'linkedin':
        return `${baseContent}\n\n👤 Gast: ${guest}\n⏱️ ${duration} minuten\n📅 ${date}${links}\n\n${hashtags}\n\nSuperKonnected - Van netwerken naar verbinden`;
      
      case 'twitter':
        return `${baseContent}\n\n👤 ${guest}\n⏱️ ${duration}min\n📅 ${date}${links}\n\n${hashtags}`;
      
      case 'facebook':
        return `${baseContent}\n\n👤 Gast: ${guest}\n⏱️ ${duration} minuten\n📅 ${date}${links}\n\nSuperKonnected - Van netwerken naar verbinden`;
      
      case 'instagram':
        return `${baseContent}\n\n👤 ${guest}\n⏱️ ${duration}min\n📅 ${date}\n\n${hashtags}`;
      
      case 'tiktok':
        return `${baseContent}\n\n👤 ${guest}\n⏱️ ${duration}min\n📅 ${date}\n\n${hashtags}\n\n#fyp #viral #trending`;
      
      default:
        return baseContent;
    }
  }

  // Generate social media content for content feed items
  generateContentFeedContent(item: any, platform: 'linkedin' | 'twitter' | 'facebook' | 'instagram'): string {
    const baseContent = `${item.title}\n\n${item.content}`;
    const author = item.author;
    const hashtags = item.tags.map((tag: string) => `#${tag}`).join(' ');
    const website = 'superkonnected.nl';
    
    switch (platform) {
      case 'linkedin':
        return `${baseContent}\n\n👤 Door: ${author}\n\n${hashtags}\n\nJoin de community: ${website}`;
      
      case 'twitter':
        return `${baseContent}\n\n👤 ${author}\n\n${hashtags}`;
      
      case 'facebook':
        return `${baseContent}\n\n👤 Door: ${author}\n\nSuperKonnected - Van netwerken naar verbinden`;
      
      case 'instagram':
        return `${baseContent}\n\n👤 ${author}\n\n${hashtags}`;
      
      default:
        return baseContent;
    }
  }

  // Process automatic sharing for a user
  async processAutomaticSharing(userId: string): Promise<void> {
    try {
      // Check post limit before automatic sharing
      const { default: postLimitService } = await import('./postLimitService');
      const limitCheck = await postLimitService.checkPostLimit(userId);
      if (!limitCheck.canPost) {
        console.log(`User ${userId} has reached post limit, skipping automatic sharing`);
        return;
      }

      // Get user profile
      const userQuery = query(collection(db, 'userProfiles'), where('uid', '==', userId));
      const userSnapshot = await getDocs(userQuery);
      
      if (userSnapshot.empty) return;
      
      const userProfile = userSnapshot.docs[0].data() as UserProfile;
      
      if (!userProfile.socialSharingEnabled) return;

      // Check if user has connected social media accounts
      const connectedPlatforms = userProfile.socialMediaConnections?.filter(conn => conn.isConnected) || [];
      if (connectedPlatforms.length === 0) {
        console.log('❌ No social media accounts connected for user:', userId);
        return;
      }

      // Check if it's time to share based on frequency
      const now = new Date();
      const lastShared = userProfile.lastSharedDate ? new Date(userProfile.lastSharedDate) : null;
      
      if (lastShared) {
        const daysSinceLastShare = Math.floor((now.getTime() - lastShared.getTime()) / (1000 * 60 * 60 * 24));
        const sharesPerWeek = userProfile.sharingFrequency;
        const daysBetweenShares = 7 / sharesPerWeek;
        
        if (daysSinceLastShare < daysBetweenShares) return;
      }

      // Vooraf gedefinieerde content feed items voor automatische sharing
      const contentFeedItems = [
        {
          id: '1',
          type: 'podcast',
          title: '🎙️ Nieuwe SuperKonnected Episode: "Van Netwerken naar Verbinden"',
          content: 'In deze aflevering bespreken we hoe je van oppervlakkig netwerken naar echte verbindingen gaat. Met gasten Abdul Antonio en Sarah Johnson.',
          author: 'SuperKonnected Team',
          category: 'Podcast',
          tags: ['netwerken', 'verbinden', 'ondernemers', 'podcast']
        },
        {
          id: '2',
          type: 'tip',
          title: '💡 Tip van de Dag: Effectief Netwerken',
          content: 'De beste netwerkers luisteren meer dan ze praten. Stel open vragen en toon oprechte interesse in de ander.',
          author: 'SuperKonnected',
          category: 'Tips',
          tags: ['netwerken', 'tips', 'communicatie']
        },
        {
          id: '3',
          type: 'quote',
          title: '💬 Quote: "Netwerken is niet wat je weet, maar wie je kent"',
          content: 'De kracht van netwerken ligt niet in je kennis, maar in je connecties. Investeer in relaties, niet alleen in vaardigheden.',
          author: 'Abdul Antonio',
          category: 'Quote',
          tags: ['quote', 'netwerken', 'relaties']
        },
        {
          id: '4',
          type: 'event',
          title: '🎉 SuperKonnected Community Event: "Ondernemers Meetup"',
          content: 'Kom naar onze maandelijkse meetup! Ontmoet andere ondernemers, deel ervaringen en bouw waardevolle connecties.',
          author: 'SuperKonnected Events',
          category: 'Event',
          tags: ['event', 'meetup', 'ondernemers', 'netwerken']
        },
        {
          id: '5',
          type: 'achievement',
          title: '🏆 Community Achievement: 1000+ Leden Bereikt!',
          content: 'SuperKonnected heeft de mijlpaal van 1000+ community leden bereikt! Samen bouwen we aan het grootste ondernemersnetwerk van Nederland.',
          author: 'SuperKonnected Team',
          category: 'Achievement',
          tags: ['achievement', 'mijlpaal', 'community', 'groei']
        }
      ];

      // Kies willekeurige content uit de feed
      const randomItem = contentFeedItems[Math.floor(Math.random() * contentFeedItems.length)];
      
      // Kies willekeurig platform uit gekoppelde platforms
      const randomPlatform = connectedPlatforms[Math.floor(Math.random() * connectedPlatforms.length)].platform;
      
      // Genereer content voor het gekozen platform
      const content = this.generateContentFeedContent(randomItem, randomPlatform);
      const pointsEarned = 2; // Verfijnd model - 2 punten per automatische share

      // Create share record
      const shareData: Omit<SocialShare, 'id'> = {
        userId: userProfile.uid,
        userName: userProfile.displayName,
        platform: randomPlatform,
        content,
        timestamp: now,
        pointsEarned,
        status: 'pending'
      };

      const shareRef = await addDoc(collection(db, 'socialShares'), shareData);

      // Attempt to share
      let shareResult;
      switch (randomPlatform) {
        case 'linkedin':
          shareResult = await this.shareToLinkedIn(content, userProfile.linkedinAccessToken);
          break;
        case 'twitter':
          shareResult = await this.shareToTwitter(content, userProfile.twitterAccessToken);
          break;
        case 'facebook':
          shareResult = await this.shareToFacebook(content, userProfile.facebookAccessToken);
          break;
        default:
          shareResult = { success: false, error: 'Platform not supported' };
      }

      // Update share status
      await updateDoc(doc(db, 'socialShares', shareRef.id), {
        status: shareResult.success ? 'shared' : 'failed',
        shareUrl: shareResult.shareUrl
      });

      // Update user points if successful (verfijnd model met rate limiting)
      if (shareResult.success && pointsEarned > 0) {
        // Check daily share limit (max 3 shares per dag tellen mee voor punten)
        const today = new Date().toDateString();
        const lastSharedDate = userProfile.lastSharedDate;
        const lastShareDate = lastSharedDate ? (typeof lastSharedDate === 'string' ? new Date(lastSharedDate).toDateString() : lastSharedDate.toDateString()) : '';
        const dailyShareCount = (lastShareDate === today) ? ((userProfile as any).dailyShareCount || 0) : 0;
        
        if (dailyShareCount < 3) {
          await updateDoc(doc(db, 'userProfiles', userProfile.uid), {
            points: (userProfile.points || 0) + pointsEarned,
            totalShares: (userProfile.totalShares || 0) + 1,
            lastSharedDate: new Date(),
            dailyShareCount: dailyShareCount + 1
          } as any);
        } else {
          // Alleen stats bijwerken, geen punten (limiet bereikt)
          await updateDoc(doc(db, 'userProfiles', userProfile.uid), {
            totalShares: (userProfile.totalShares || 0) + 1,
            lastSharedDate: now
          });
        }
      } else if (shareResult.success) {
        // Alleen stats bijwerken als geen punten
        await updateDoc(doc(db, 'userProfiles', userProfile.uid), {
          totalShares: (userProfile.totalShares || 0) + 1,
          lastSharedDate: now
        });
      }

    } catch (error) {
      console.error('Error processing automatic sharing:', error);
    }
  }

  // Manual sharing function
  async manualShare(
    userId: string, 
    platform: 'linkedin' | 'twitter' | 'facebook' | 'instagram', 
    session: PodcastSession
  ): Promise<{ success: boolean; shareUrl?: string; error?: string }> {
    try {
      // Get user profile
      const userQuery = query(collection(db, 'userProfiles'), where('uid', '==', userId));
      const userSnapshot = await getDocs(userQuery);
      
      if (userSnapshot.empty) {
        return { success: false, error: 'User not found' };
      }
      
      const userProfile = userSnapshot.docs[0].data() as UserProfile;
      const content = this.generateSocialContent(session, platform);
      const pointsEarned = 2; // Verfijnd model - 2 punten per handmatige share

      // Create share record
      const shareData: Omit<SocialShare, 'id'> = {
        userId: userProfile.uid,
        userName: userProfile.displayName,
        platform,
        content,
        timestamp: new Date(),
        pointsEarned,
        status: 'pending'
      };

      const shareRef = await addDoc(collection(db, 'socialShares'), shareData);

      // Attempt to share
      let shareResult;
      switch (platform) {
        case 'linkedin':
          shareResult = await this.shareToLinkedIn(content, userProfile.linkedinAccessToken);
          break;
        case 'twitter':
          shareResult = await this.shareToTwitter(content, userProfile.twitterAccessToken);
          break;
        case 'facebook':
          shareResult = await this.shareToFacebook(content, userProfile.facebookAccessToken);
          break;
        case 'instagram':
          shareResult = await this.shareToInstagram(content, userProfile.instagramAccessToken);
          break;
        default:
          shareResult = { success: false, error: 'Platform not supported' };
      }

      // Update share status
      await updateDoc(doc(db, 'socialShares', shareRef.id), {
        status: shareResult.success ? 'shared' : 'failed',
        shareUrl: shareResult.shareUrl
      });

      // Update user points if successful (verfijnd model met rate limiting)
      if (shareResult.success && pointsEarned > 0) {
        // Check daily share limit (max 3 shares per dag tellen mee voor punten)
        const today = new Date().toDateString();
        const lastSharedDate = userProfile.lastSharedDate;
        const lastShareDate = lastSharedDate ? (typeof lastSharedDate === 'string' ? new Date(lastSharedDate).toDateString() : lastSharedDate.toDateString()) : '';
        const dailyShareCount = (lastShareDate === today) ? ((userProfile as any).dailyShareCount || 0) : 0;
        
        if (dailyShareCount < 3) {
          await updateDoc(doc(db, 'userProfiles', userProfile.uid), {
            points: (userProfile.points || 0) + pointsEarned,
            totalShares: (userProfile.totalShares || 0) + 1,
            lastSharedDate: new Date(),
            dailyShareCount: dailyShareCount + 1
          } as any);
        } else {
          // Alleen stats bijwerken, geen punten (limiet bereikt)
          await updateDoc(doc(db, 'userProfiles', userProfile.uid), {
            totalShares: (userProfile.totalShares || 0) + 1,
            lastSharedDate: new Date()
          });
        }
      } else if (shareResult.success) {
        // Alleen stats bijwerken als geen punten
        await updateDoc(doc(db, 'userProfiles', userProfile.uid), {
          totalShares: (userProfile.totalShares || 0) + 1,
          lastSharedDate: new Date()
        });
      }

      return shareResult;

    } catch (error) {
      console.error('Error in manual share:', error);
      return { success: false, error: 'Sharing failed' };
    }
  }

  // Get sharing statistics
  async getSharingStats(userId: string): Promise<{
    totalShares: number;
    totalPoints: number;
    sharesThisWeek: number;
    platformBreakdown: Record<string, number>;
  }> {
    try {
      const sharesQuery = query(collection(db, 'socialShares'), where('userId', '==', userId));
      const sharesSnapshot = await getDocs(sharesQuery);
      
      const shares = sharesSnapshot.docs.map(doc => doc.data() as SocialShare);
      
      const now = new Date();
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      
      const sharesThisWeek = shares.filter(share => 
        new Date(share.timestamp) >= weekAgo
      ).length;

      const platformBreakdown = shares.reduce((acc, share) => {
        acc[share.platform] = (acc[share.platform] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      const totalPoints = shares.reduce((sum, share) => sum + share.pointsEarned, 0);

      return {
        totalShares: shares.length,
        totalPoints,
        sharesThisWeek,
        platformBreakdown
      };

    } catch (error) {
      console.error('Error getting sharing stats:', error);
      return {
        totalShares: 0,
        totalPoints: 0,
        sharesThisWeek: 0,
        platformBreakdown: {}
      };
    }
  }

  // Get all users with automatic sharing enabled
  async getActiveUsers(): Promise<UserProfile[]> {
    try {
      const usersQuery = query(
        collection(db, 'userProfiles'),
        where('socialSharingEnabled', '==', true)
      );
      const usersSnapshot = await getDocs(usersQuery);
      
      return usersSnapshot.docs.map(doc => ({
        ...doc.data(),
        uid: doc.data().uid || doc.id
      })) as UserProfile[];
    } catch (error) {
      console.error('Error getting active users:', error);
      return [];
    }
  }

  // Get user posts for SocialSharing component
  async getUserPosts(userId: string): Promise<SocialPost[]> {
    try {
      const postsQuery = query(
        collection(db, 'socialPosts'),
        where('userId', '==', userId),
        orderBy('scheduledDate', 'desc')
      );
      const postsSnapshot = await getDocs(postsQuery);
      
      return postsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        scheduledDate: doc.data().scheduledDate.toDate()
      })) as SocialPost[];
    } catch (error) {
      console.error('Error getting user posts:', error);
      return [];
    }
  }

  // Get social analytics for SocialSharing component
  async getSocialAnalytics(userId: string): Promise<SocialAnalytics> {
    try {
      const postsQuery = query(
        collection(db, 'socialPosts'),
        where('userId', '==', userId)
      );
      const postsSnapshot = await getDocs(postsQuery);
      
      const posts = postsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        scheduledDate: doc.data().scheduledDate.toDate()
      })) as SocialPost[];

      const totalPosts = posts.length;
      const totalEngagement = posts.reduce((sum, post) => 
        sum + (post.engagement?.likes || 0) + (post.engagement?.shares || 0) + (post.engagement?.comments || 0), 0
      );
      const averageEngagement = totalPosts > 0 ? Math.round(totalEngagement / totalPosts) : 0;

      const platformBreakdown = posts.reduce((acc, post) => {
        acc[post.platform] = (acc[post.platform] || 0) + 1;
        return acc;
      }, {
        linkedin: 0,
        twitter: 0,
        instagram: 0,
        facebook: 0
      });

      const topPerformingPost = posts.reduce((top, post) => {
        const postEngagement = (post.engagement?.likes || 0) + (post.engagement?.shares || 0) + (post.engagement?.comments || 0);
        const topEngagement = (top?.engagement?.likes || 0) + (top?.engagement?.shares || 0) + (top?.engagement?.comments || 0);
        return postEngagement > topEngagement ? post : top;
      }, null as SocialPost | null);

      return {
        totalPosts,
        totalEngagement,
        averageEngagement,
        topPerformingPost,
        platformBreakdown
      };
    } catch (error) {
      console.error('Error getting social analytics:', error);
      return {
        totalPosts: 0,
        totalEngagement: 0,
        averageEngagement: 0,
        topPerformingPost: null,
        platformBreakdown: {
          linkedin: 0,
          twitter: 0,
          instagram: 0,
          facebook: 0
        }
      };
    }
  }

  // Create new post for SocialSharing component
  async createPost(userId: string, postData: {
    content: string;
    platform: SocialPost['platform'];
    scheduledDate: Date;
  }): Promise<SocialPost> {
    try {
      const postDoc = {
        userId,
        content: postData.content,
        platform: postData.platform,
        scheduledDate: postData.scheduledDate,
        status: 'draft' as const,
        createdAt: new Date()
      };

      const docRef = await addDoc(collection(db, 'socialPosts'), postDoc);
      
      return {
        id: docRef.id,
        content: postData.content,
        platform: postData.platform,
        scheduledDate: postData.scheduledDate,
        status: 'draft'
      };
    } catch (error) {
      console.error('Error creating post:', error);
      throw error;
    }
  }

  // Delete post for SocialSharing component
  async deletePost(postId: string): Promise<void> {
    try {
      await updateDoc(doc(db, 'socialPosts', postId), {
        status: 'deleted',
        deletedAt: new Date()
      });
    } catch (error) {
      console.error('Error deleting post:', error);
      throw error;
    }
  }

  // Trigger automatic sharing for all users when a new episode is published
  async triggerEpisodeSharing(episodeId: string): Promise<void> {
    try {
      console.log(`🎧 Triggering episode sharing for episode: ${episodeId}`);
      
      // Get all users with automatic sharing enabled
      const activeUsers = await this.getActiveUsers();
      
      // Get the specific episode that was just published
      const episodeQuery = query(collection(db, 'episodes'), where('__name__', '==', episodeId));
      const episodeSnapshot = await getDocs(episodeQuery);
      
      if (episodeSnapshot.empty) {
        console.error(`❌ Episode ${episodeId} not found`);
        return;
      }
      
      const episode = episodeSnapshot.docs[0].data();
      const platforms: Array<'linkedin' | 'twitter' | 'facebook' | 'instagram'> = ['linkedin', 'twitter', 'facebook'];
      
      // Force share this specific episode to all active users
      for (const user of activeUsers) {
        try {
          // Get user profile
          const userQuery = query(collection(db, 'userProfiles'), where('uid', '==', user.uid));
          const userSnapshot = await getDocs(userQuery);
          
          if (userSnapshot.empty) continue;
          
          const userProfile = userSnapshot.docs[0].data() as UserProfile;
          
          if (!userProfile.socialSharingEnabled) continue;
          
          // Generate content for this specific episode
          const randomPlatform = platforms[Math.floor(Math.random() * platforms.length)];
          const content = this.generateEpisodeContent(episode, randomPlatform);
          const pointsEarned = 2; // Verfijnd model - 2 punten per episode share
          const now = new Date();
          
          // Create share record
          const shareData: Omit<SocialShare, 'id'> = {
            userId: userProfile.uid,
            userName: userProfile.displayName,
            platform: randomPlatform,
            content,
            timestamp: now,
            pointsEarned,
            status: 'pending'
          };
          
          const shareRef = await addDoc(collection(db, 'socialShares'), shareData);
          
          // Attempt to share
          let shareResult;
          switch (randomPlatform) {
            case 'linkedin':
              shareResult = await this.shareToLinkedIn(content, userProfile.linkedinAccessToken);
              break;
            case 'twitter':
              shareResult = await this.shareToTwitter(content, userProfile.twitterAccessToken);
              break;
            case 'facebook':
              shareResult = await this.shareToFacebook(content, userProfile.facebookAccessToken);
              break;
            default:
              shareResult = { success: false, error: 'Platform not supported' };
          }
          
          // Update share status
          await updateDoc(doc(db, 'socialShares', shareRef.id), {
            status: shareResult.success ? 'shared' : 'failed',
            shareUrl: shareResult.shareUrl
          });
          
          // Update user points if successful (verfijnd model met rate limiting)
          if (shareResult.success && pointsEarned > 0) {
            // Check daily share limit (max 3 shares per dag tellen mee voor punten)
            const today = new Date().toDateString();
            const lastSharedDate = userProfile.lastSharedDate;
            const lastShareDate = lastSharedDate ? (typeof lastSharedDate === 'string' ? new Date(lastSharedDate).toDateString() : lastSharedDate.toDateString()) : '';
            const dailyShareCount = (lastShareDate === today) ? ((userProfile as any).dailyShareCount || 0) : 0;
            
            if (dailyShareCount < 3) {
              await updateDoc(doc(db, 'userProfiles', userProfile.uid), {
                points: (userProfile.points || 0) + pointsEarned,
                totalShares: (userProfile.totalShares || 0) + 1,
                lastSharedDate: new Date(),
                dailyShareCount: dailyShareCount + 1
              } as any);
            } else {
              // Alleen stats bijwerken, geen punten (limiet bereikt)
              await updateDoc(doc(db, 'userProfiles', userProfile.uid), {
                totalShares: (userProfile.totalShares || 0) + 1,
                lastSharedDate: now
              });
            }
          } else if (shareResult.success) {
            // Alleen stats bijwerken als geen punten
            await updateDoc(doc(db, 'userProfiles', userProfile.uid), {
              totalShares: (userProfile.totalShares || 0) + 1,
              lastSharedDate: now
            });
          }
          
          console.log(`✅ Shared episode "${episode.title}" for user: ${user.displayName} on ${randomPlatform}`);
        } catch (error) {
          console.error(`❌ Error sharing episode for user ${user.displayName}:`, error);
        }
      }
      
      console.log(`🎉 Episode "${episode.title}" shared to ${activeUsers.length} community members!`);
    } catch (error) {
      console.error('Error triggering episode sharing:', error);
    }
  }
}

export const socialSharingService = new SocialSharingService();
